import { useLayoutEffect, useRef, useState } from "react"
import styled from "styled-components"
import { formatDisplayNumber, getDisplayCalories } from "utils"
import { IWaterWeeklyChartDataItem } from "../../types"

const TOOLTIP_TOP = 24
const LINE_TOP = 72
// ChartSection padding-top (16) + BarChart margin-top (6)
const GAP_TO_PLOT_AREA = 22
// FlexBox height (154) - BarChart margin-top (6) - XAxis height (18)
const PLOT_AREA_HEIGHT = 130
const SIDE_PADDING = 24
const Y_AXIS_WIDTH = 40
const X_AXIS_PADDING = 16
const NUM_BARS = 7

const FloatingArea = styled.div`
  position: relative;
  height: 72px;
  overflow: visible;
`

const Card = styled.div`
  position: absolute;
  top: ${TOOLTIP_TOP}px;
  z-index: 2;
  display: flex;
  flex-direction: column;
  gap: 2px;
  align-items: flex-start;
  justify-content: center;
  backdrop-filter: blur(15px);
  white-space: nowrap;
  pointer-events: none;
`

const DateText = styled.div`
  font-size: 14px;
  font-weight: 400;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.6);
`

const ValueRow = styled.div`
  display: flex;
  gap: 4px;
  align-items: baseline;
`

const Value = styled.span`
  font-size: 20px;
  font-weight: 700;
  line-height: 1.2;
  color: white;
`

const Unit = styled.span`
  font-size: 16px;
  font-weight: 700;
  line-height: 1.2;
  color: rgba(255, 255, 255, 0.6);
`

const VerticalLine = styled.div`
  position: absolute;
  top: ${LINE_TOP}px;
  width: 1px;
  background: rgba(255, 255, 255, 0.2);
  pointer-events: none;
  z-index: 1;
`

const computeBarScreenX = (barIndex: number, containerWidth: number) => {
  const svgWidth = containerWidth - SIDE_PADDING * 2
  const plotAreaWidth = svgWidth - Y_AXIS_WIDTH
  const usableWidth = plotAreaWidth - X_AXIS_PADDING * 2
  const barCoordX = X_AXIS_PADDING + barIndex * (usableWidth / (NUM_BARS - 1))
  return SIDE_PADDING + barCoordX
}

const WaterTooltip = ({
  activeData,
  barIndex,
  unit = "ml",
  maxYAxis,
}: {
  activeData: IWaterWeeklyChartDataItem
  barIndex: number
  unit?: "ml" | "oz"
  maxYAxis: number
}) => {
  const areaRef = useRef<HTMLDivElement>(null)
  const cardRef = useRef<HTMLDivElement>(null)
  const [layout, setLayout] = useState<{
    cardLeft: number
    lineLeft: number
  } | null>(null)

  useLayoutEffect(() => {
    const area = areaRef.current
    const card = cardRef.current
    if (!area || !card) return

    const containerWidth = area.offsetWidth
    const barScreenX = computeBarScreenX(barIndex, containerWidth)
    const cardWidth = card.offsetWidth
    const halfCard = cardWidth / 2

    const cardLeft = Math.max(
      SIDE_PADDING,
      Math.min(
        barScreenX - halfCard,
        containerWidth - SIDE_PADDING - cardWidth,
      ),
    )

    setLayout({ cardLeft, lineLeft: barScreenX })
  }, [barIndex])

  const amount = activeData.amount

  const EMPTY_DAY_LINE_HEIGHT = 152
  const lineHeight =
    amount === 0
      ? EMPTY_DAY_LINE_HEIGHT
      : maxYAxis > 0
      ? GAP_TO_PLOT_AREA + PLOT_AREA_HEIGHT * (1 - amount / maxYAxis)
      : EMPTY_DAY_LINE_HEIGHT

  return (
    <FloatingArea ref={areaRef}>
      <Card
        ref={cardRef}
        style={{
          left: layout?.cardLeft ?? 0,
          opacity: layout ? 1 : 0,
        }}
      >
        <DateText>{activeData.date}</DateText>
        <ValueRow>
          <Value>{formatDisplayNumber(amount, unit === "oz" ? 1 : 0)}</Value>
          <Unit>{unit}</Unit>
        </ValueRow>
      </Card>
      {layout && (
        <VerticalLine style={{ left: layout.lineLeft, height: lineHeight }} />
      )}
    </FloatingArea>
  )
}

export default WaterTooltip
